import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defaultConfig } from './lib/defaults';
import { humanDuration } from './lib/duration';
import { generateTasks } from './lib/flux';
import { decodeConfig, encodeConfig } from './lib/permalink';
import { resolvePlan } from './lib/resolve';
import { estimateStorage } from './lib/storage';
import type { DesignConfig } from './lib/types';
import { analyse } from './lib/warnings';
import FluxPanel from './components/FluxPanel';
import Footer from './components/Footer';
import SavingsPanel from './components/SavingsPanel';
import SchedulePanel from './components/SchedulePanel';
import SourcePanel from './components/SourcePanel';
import ThemeToggle from './components/ThemeToggle';
import TiersPanel from './components/TiersPanel';
import WarningsPanel from './components/WarningsPanel';

interface InitialState {
  config: DesignConfig;
  /** Set when a hash was present but could not be read. */
  hashError: string | null;
}

/** Read the config out of the URL hash, falling back to the worked example. */
function initialState(): InitialState {
  const hash = typeof window === 'undefined' ? '' : window.location.hash;
  if (hash.replace(/^#/, '') === '') return { config: defaultConfig(), hashError: null };
  const result = decodeConfig(hash);
  if (result.ok) return { config: result.config, hashError: null };
  return { config: defaultConfig(), hashError: result.error };
}

export default function App() {
  const [{ config: startConfig, hashError }] = useState(initialState);
  const [config, setConfig] = useState<DesignConfig>(startConfig);
  const [dismissedHashError, setDismissedHashError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const notify = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  }, []);

  useEffect(() => () => void (toastTimer.current && clearTimeout(toastTimer.current)), []);

  const plan = useMemo(() => resolvePlan(config), [config]);
  const estimate = useMemo(
    () => estimateStorage(plan, config.assumptions),
    [plan, config.assumptions],
  );
  const warnings = useMemo(() => analyse(config, plan, estimate), [config, plan, estimate]);
  const tasks = useMemo(() => generateTasks(config, plan), [config, plan]);
  const invalidPaths = useMemo(
    () => new Set(plan.parseErrors.map((e) => e.path)),
    [plan.parseErrors],
  );

  // Keep the address bar in sync so the page can be shared at any moment, without
  // pushing history entries for every keystroke.
  useEffect(() => {
    const hash = encodeConfig(config);
    if (window.location.hash.replace(/^#/, '') !== hash) {
      window.history.replaceState(null, '', `#${hash}`);
    }
  }, [config]);

  const sourceLabels = plan.tiers.map((tier) =>
    tier.fromRaw
      ? `${plan.source.bucket} (${humanDuration(tier.sourceResolutionSeconds)} raw)`
      : `${tier.sourceBucket} (${humanDuration(tier.sourceResolutionSeconds)})`,
  );

  const shareLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#${encodeConfig(config)}`;
    try {
      await navigator.clipboard.writeText(url);
      notify('Permalink copied to the clipboard');
    } catch {
      notify('Copy blocked — the link is in the address bar');
    }
  };

  return (
    <>
      <a className="skip-link" href="#results">
        Skip to results
      </a>
      <div className="app">
        <header className="masthead">
          <div>
            <h1>Downsample Designer</h1>
            <p>
              Describe your raw resolution and the granularity you need long term. Get the InfluxDB
              Flux tasks that implement the cascade, an auditable storage estimate, and a check for
              the misconfigurations that quietly corrupt a rollup.
            </p>
          </div>
          <div className="masthead-actions">
            <button type="button" data-testid="share" className="primary" onClick={shareLink}>
              Copy permalink
            </button>
            <button
              type="button"
              data-testid="reset"
              onClick={() => {
                setConfig(defaultConfig());
                notify('Reset to the worked example');
              }}
            >
              Reset
            </button>
            <ThemeToggle />
          </div>
        </header>

        {hashError && !dismissedHashError ? (
          <div className="panel" style={{ marginBottom: 20 }} data-testid="hash-error">
            <div className="finding error">
              <span className="badge">error</span>
              <div>
                <h3>That permalink could not be read</h3>
                <p>
                  {hashError}. The worked example below has been loaded instead — nothing was lost,
                  the link simply did not decode.
                </p>
                <button
                  type="button"
                  style={{ marginTop: 8 }}
                  onClick={() => setDismissedHashError(true)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="columns">
          <div className="stack">
            <SourcePanel
              source={config.source}
              onChange={(source) => setConfig({ ...config, source })}
              invalidPaths={invalidPaths}
              seriesCount={plan.source.seriesCount}
              retainedSeriesCount={plan.source.retainedSeriesCount}
            />
            <TiersPanel
              tiers={config.tiers}
              onChange={(tiers) => setConfig({ ...config, tiers })}
              invalidPaths={invalidPaths}
              sourceLabels={sourceLabels}
            />
            <SchedulePanel
              schedule={config.schedule}
              assumptions={config.assumptions}
              invalidPaths={invalidPaths}
              onScheduleChange={(schedule) => setConfig({ ...config, schedule })}
              onAssumptionsChange={(assumptions) => setConfig({ ...config, assumptions })}
            />
          </div>

          <div className="stack" id="results">
            <WarningsPanel warnings={warnings} />
            <SavingsPanel estimate={estimate} />
            <FluxPanel tasks={tasks} onNotify={notify} />
          </div>
        </div>

        <Footer />
      </div>

      <div aria-live="polite" role="status">
        {toast ? <div className="toast">{toast}</div> : null}
      </div>
    </>
  );
}
